/**
 * INT-0009 — Appearance page (System & Tools). Thin shell around the shared
 * useAppearanceEditor hook (also used by the create-flow design step): this
 * page contributes the rail-mode chrome, the STICKY header-slot Save button,
 * the unsaved-changes guard, and the dynamic-squish grid; the hook owns
 * every control and the live preview.
 */
import { useEffect } from 'react';
import { useOutletContext, useBlocker } from 'react-router-dom';
import { Palette } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { PageContainer, PageHeader } from '@/components/Page/PageContainer';
import { ListSkeleton } from '@/components/Page/PageSkeletons';
import useAdminStore from '@store/adminStore';
import useAppearanceEditor from '@/components/AppearanceEditor/useAppearanceEditor';

type AnyDoc = Record<string, any>;

export default function AppearanceAdmin() {
  const { league } = useOutletContext() as { leagueName: string; league: AnyDoc | null };
  const { admin } = useAdminStore((state: any) => ({ admin: state.admin }));
  // Tabbed layout (owner 2026-09-25): Site design / Logo / Banner are
  // exclusive subsections, each with its OWN save inside `controls` — the
  // page header carries no global Save anymore.
  const { controls, preview, saving, isDirty, mobilePreview } = useAppearanceEditor({ league, admin, layout: 'tabbed' });

  // Unsaved-changes guards (owner 2026-09-09): in-app navigation goes
  // through the data router's blocker + our dialog; tab close/refresh gets
  // the browser's native beforeunload prompt (not customizable).
  const blocker = useBlocker(isDirty && !saving);
  useEffect(() => {
    if (!isDirty) return undefined;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  if (!league) {
    return (
      <PageContainer fullWidth>
        <PageHeader
          title={(
            <span className="flex items-center space-x-2">
              <Palette className="w-5 h-5 text-primary" />
              <span>Appearance</span>
            </span>
          )}
          description="Templates, colors, and fonts for your public site"
        />
        <ListSkeleton rows={4} />
      </PageContainer>
    );
  }

  return (
    <PageContainer fullWidth>
      {/* Sticky header (owner 2026-09-09): title + Save stay visible while
          scrolling the controls column. Negative margins + padding keep the
          backdrop flush with the page gutters. */}
      {/* -mx-6/-mt-4 match .league-main-content's 2rem/1.5rem gutters so the
          backdrop spans edge-to-edge while sticking. */}
      <div className="sticky top-0 z-30 -mx-6 px-6 -mt-4 pt-4 pb-3 mb-3 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80 border-b border-border">
        <PageHeader
          title={(
            <span className="flex items-center space-x-2">
              <Palette className="w-5 h-5 text-primary" />
              <span>Appearance</span>
            </span>
          )}
          description="Templates, colors, and fonts for your public site"
        />
      </div>
      {/* Dynamic squish (owner 2026-09-07): the desktop/mobile preview
          toggle resizes BOTH columns — desktop preview needs the width
          (controls squish), the 390px mobile preview doesn't. */}
      <div className="grid grid-cols-1 xl:grid-cols-10 gap-6 items-start">
        <div className={`space-y-6 ${mobilePreview ? 'xl:col-span-6' : 'xl:col-span-3'}`}>
          {controls}
        </div>
        <div className={`${mobilePreview ? 'xl:col-span-4' : 'xl:col-span-7'} xl:sticky xl:top-20`}>
          {preview}
        </div>
      </div>

      <AlertDialog open={blocker.state === 'blocked'}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Leave without saving?</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved appearance changes. If you leave now they'll be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => blocker.reset?.()}>Stay on this page</AlertDialogCancel>
            <AlertDialogAction onClick={() => blocker.proceed?.()}>Leave without saving</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </PageContainer>
  );
}
